import React, { useEffect, useState } from 'react';
import Section from './Section';
import Icon from './Icons';
import ContributionLandscape from './ContributionLandscape';
import { PROFILE } from '../data/profile';
import { LANGUAGES as FALLBACK_LANGS, REPOS as FALLBACK_REPOS, PRODUCT_LINKS, HIDDEN_REPOS } from '../data/github';

const LANG_COLORS = {
  Python: '#3572A5', Java: '#b07219', 'C++': '#f34b7d', C: '#555555', Go: '#00ADD8', R: '#198CE7',
  JavaScript: '#f1e05a', TypeScript: '#3178c6', Kotlin: '#A97BFF', Dart: '#00B4AB', SQL: '#e38c00',
  HCL: '#844FBA', HTML: '#e34c26', CSS: '#563d7c', Shell: '#89e051', 'Jupyter Notebook': '#DA5B0B', PLpgSQL: '#336790',
};
const color = (name, given) => given || LANG_COLORS[name] || '#8b8b8b';

/** Live data from /api/github (server-side token); falls back to the public contributions API + curated data. */
function useGitHub(user) {
  const [state, setState] = useState({ loading: true, live: false, data: null });
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await fetch('/api/github');
        if (r.ok) {
          const j = await r.json();
          if (!j.error && Array.isArray(j.contributions)) { if (!dead) setState({ loading: false, live: true, data: j }); return; }
        }
      } catch (e) { /* fall through */ }
      try {
        const r = await fetch(`https://github-contributions-api.jogruber.de/v4/${user}?y=last`);
        const j = r.ok ? await r.json() : null;
        const total = j?.total ? Object.values(j.total).reduce((a, b) => a + b, 0) : 0;
        if (!dead) setState({ loading: false, live: false, data: { contributions: j?.contributions || [], total, languages: FALLBACK_LANGS, repos: FALLBACK_REPOS } });
      } catch (e) {
        if (!dead) setState({ loading: false, live: false, data: { contributions: [], total: 0, languages: FALLBACK_LANGS, repos: FALLBACK_REPOS } });
      }
    })();
    return () => { dead = true; };
  }, [user]);
  return state;
}

function Languages({ langs, hidden }) {
  const [open, setOpen] = useState(null);
  const [hoverSeg, setHoverSeg] = useState(null);
  if (!langs?.length) return <p className="github-empty">No language data available.</p>;
  const shown = langs.reduce((s, l) => s + l.pct, 0);
  const other = Math.max(0, Math.round((100 - shown) * 10) / 10);
  return (
    <>
      <div className="langbar-wrap">
        <div className="langbar" onMouseLeave={() => setHoverSeg(null)}>
          {langs.map((l) => (
            <span key={l.name} className="langbar__seg" style={{ width: `${l.pct}%`, background: color(l.name, l.color) }}
              onMouseEnter={() => setHoverSeg(l)} onClick={() => setOpen(open === l.name ? null : l.name)} />
          ))}
          {other > 0 && <span className="langbar__seg" style={{ width: `${other}%`, background: '#8b8b8b' }} onMouseEnter={() => setHoverSeg({ name: 'Other', pct: other })} />}
        </div>
        {hoverSeg && (
          <div className="langbar__tip"><i style={{ background: color(hoverSeg.name, hoverSeg.color) }} />{hoverSeg.name} <b>{hoverSeg.pct}%</b></div>
        )}
      </div>
      <div className="langgrid">
        {langs.map((l) => {
          const isOpen = open === l.name;
          const repos = (l.repos || []).filter((r) => !hidden.has(r.toLowerCase()));
          return (
            <div key={l.name} className={`langcard${isOpen ? ' langcard--open' : ''}`}>
              <button className="langcard__head" onClick={() => setOpen(isOpen ? null : l.name)} aria-expanded={isOpen}>
                <i className="langcard__dot" style={{ background: color(l.name, l.color) }} />
                <span className="langcard__name">{l.name}</span>
                <span className="langcard__pct">{l.pct}%</span>
                <span className="langcard__div" />
                <span className="langcard__count">{repos.length}</span>
                <svg className="langcard__arrow" viewBox="0 0 12 12" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4.5L6 7.5L9 4.5" /></svg>
              </button>
              <div className="langcard__body"><div className="langcard__inner">
                <div className="langcard__repos">
                  {repos.length ? repos.map((r, i) => <span key={`${r}-${i}`} className="langcard__repo">{r}</span>) : <span className="github-empty">No repo data</span>}
                </div>
              </div></div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function productLink(name) {
  const n = (name || '').toLowerCase();
  return PRODUCT_LINKS.find((p) => n.includes(p.match)) || null;
}

function RepoCard({ r }) {
  const product = r.isPrivate ? productLink(r.name) : null;
  const href = r.url || product?.url || null;
  const linkLabel = r.url ? 'GitHub' : product?.label || null;
  const when = r.pushedAt ? new Date(r.pushedAt).toLocaleDateString('en', { month: 'short', year: 'numeric' }) : null;
  const Tag = href ? 'a' : 'div';
  const props = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <Tag className={`repo${r.isPrivate ? ' repo--private' : ''}`} {...props}>
      <div className="repo__head">
        <div className="repo__name">{r.name}</div>
        <span className={`repo__badge${r.isPrivate ? ' repo__badge--private' : ''}`}>{r.isPrivate ? 'Private' : 'Public'}</span>
      </div>
      <div className="repo__desc">{r.description || (r.isPrivate ? 'Private repository' : 'No description')}</div>
      <div className="repo__meta">
        {r.language && <span><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 5, background: color(r.language, r.languageColor) }} />{r.language}</span>}
        {r.commits != null && <span>{r.commits} commit{r.commits === 1 ? '' : 's'}</span>}
        {when && <span>{when}</span>}
        {linkLabel && <span className="repo__link">{linkLabel} ↗</span>}
      </div>
    </Tag>
  );
}

export default function GitHubActivity() {
  const { loading, data } = useGitHub(PROFILE.github);
  const [day, setDay] = useState(null);
  const hidden = new Set(HIDDEN_REPOS.map((n) => n.toLowerCase()));
  const repos = (data?.repos || []).filter((r) => !r.isFork && !hidden.has((r.name || '').toLowerCase()));

  return (
    <Section id="github" num="04" title="GitHub Activity" subtitle="My contribution history as a living, scrollable landscape">
      <div className="gh__links">
        <a href={PROFILE.githubUrl} target="_blank" rel="noopener noreferrer" className="btn btn--ghost btn--sm"><Icon name="github" /> GitHub</a>
      </div>

      {loading ? <div className="land land--loading"><p className="github-empty">Loading contributions…</p></div>
        : data.contributions.length ? <ContributionLandscape days={data.contributions} onDayClick={(date, count) => setDay({ date, count })} />
        : <div className="land land--loading"><p className="github-empty">Contribution data unavailable right now.</p></div>}

      {day && (() => {
        const list = ((data.byDay || {})[day.date] || []).filter((r) => !hidden.has((r.name || '').toLowerCase()));
        const nice = new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        return (
          <div className="gh__day">
            <div className="gh__day-head">
              <div><b>{nice}</b><span>{day.count} contribution{day.count === 1 ? '' : 's'}</span></div>
              <button onClick={() => setDay(null)} aria-label="Close">×</button>
            </div>
            {list.length ? (
              <div className="gh__day-list">{list.map((r, i) => {
                const product = r.isPrivate ? productLink(r.name) : null; const href = r.url || product?.url;
                const Tag = href ? 'a' : 'span'; const props = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {};
                return <Tag key={`${r.name}-${i}`} className="gh__day-repo" {...props}><i className={r.isPrivate ? 'priv' : ''} />{r.name}<b>{r.commits} commit{r.commits === 1 ? '' : 's'}</b></Tag>;
              })}</div>
            ) : <p className="github-empty">{data.byDay ? 'No commits on this day — contributions may be issues, PRs, or reviews.' : 'Per-day repo detail is available on the live site.'}</p>}
          </div>
        );
      })()}

      <div className="gh__block">
        <h3 className="gh__title"><Icon name="chart" /> Language Breakdown</h3>
        <p className="gh__sub">Languages across every repo I committed to this year (by bytes of code) — click a bar or row for the repos</p>
        {loading ? <p className="github-empty">Loading…</p> : <Languages langs={data.languages} hidden={hidden} />}
      </div>

      <div className="gh__block">
        <h3 className="gh__title"><Icon name="trending" /> What I'm Working On</h3>
        <p className="gh__sub">What I've been committing to recently — private work links to the product instead of the repo</p>
        {loading ? <p className="github-empty">Loading…</p> : repos.length ? <div className="repo-grid">{repos.map((r, i) => <RepoCard key={`${r.name}-${i}`} r={r} />)}</div> : <p className="github-empty">No repositories found.</p>}
      </div>
    </Section>
  );
}
